interface HttpError {
  response?: {
    status?: number;
    data?: unknown;
  };
  responseBody?: unknown;
}

export function isResponseError(err: unknown): err is { response?: Response } {
  return typeof err === 'object' && err !== null && 'response' in err;
}

export function getHttpErrorStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as HttpError).response;
    if (response && typeof response === 'object' && 'status' in response) {
      return response.status;
    }
  }
  return undefined;
}

export function getHttpErrorBody(err: unknown): unknown {
  if (err && typeof err === 'object' && err !== null) {
    if ('responseBody' in err) {
      const body = (err as HttpError).responseBody;
      if (body !== undefined) {
        return body;
      }
    }
    if ('response' in err) {
      const response = (err as HttpError).response;
      if (response && typeof response === 'object' && 'data' in response) {
        return response.data;
      }
    }
  }
  return undefined;
}

/** The stable `code` of a domain 4xx body, if any. */
export function getHttpErrorCode(err: unknown): string | undefined {
  const body = getHttpErrorBody(err);
  if (typeof body !== 'object' || body === null || !('code' in body)) {
    return undefined;
  }
  return typeof body.code === 'string' ? body.code : undefined;
}

export function getHttpErrorMessage(err: unknown): string | undefined {
  const body = getHttpErrorBody(err);
  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as { message: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }
  return undefined;
}

const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_INTERNAL_SERVER_ERROR = 500;
/** Nest's body for an exception it did not expect (e.g. a raw Stripe error). */
const NEST_UNHANDLED_EXCEPTION_MESSAGE = 'Internal server error';

/**
 * Bodies written by the framework, not by our translated `translateError`
 * messages: the throttler's «ThrottlerException: Too Many Requests» and Nest's
 * «Internal server error». They are English and say nothing the user can act
 * on, so the localized status text replaces them.
 */
function isFrameworkDefaultMessage(status: number | undefined, message: string): boolean {
  return (
    status === HTTP_TOO_MANY_REQUESTS ||
    (status === HTTP_INTERNAL_SERVER_ERROR && message === NEST_UNHANDLED_EXCEPTION_MESSAGE)
  );
}

export function resolveApiErrorMessage(
  err: unknown,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  const status = getHttpErrorStatus(err);
  const apiMessage = getHttpErrorMessage(err);
  if (apiMessage && !isFrameworkDefaultMessage(status, apiMessage)) {
    return apiMessage;
  }
  return t(`errors.${status ?? 'default'}`, { defaultValue: t('common:error') });
}
